import React from 'react';
import { ShieldCheckIcon, UserIcon, MapPinIcon, PhoneIcon, SettingsIcon, GlobeIcon } from 'lucide-react';
import { ownerData } from '../data/sampleData';
interface ProfilePageProps {
  t: (key: string) => string;
}
export function ProfilePage({
  t
}: ProfilePageProps) {
  return <div className="max-w-4xl mx-auto space-y-6 pb-20 lg:pb-6" data-id="element-611">
      <div data-id="element-612">
        <h2 className="text-2xl font-bold text-slate-800" data-id="element-613">
          {t('profile.title')}
        </h2>
        <p className="text-slate-500" data-id="element-614">{t('profile.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-615">
        {/* Left Column: Profile Card */}
        <div className="md:col-span-1 space-y-6" data-id="element-616">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center" data-id="element-617">
            <div className="w-24 h-24 bg-[#006A4E] text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4 shadow-inner" data-id="element-618">
              MR
            </div>
            <h3 className="text-xl font-bold text-slate-900" data-id="element-619">
              {ownerData.name}
            </h3>
            <p className="text-slate-500 text-sm mb-4" data-id="element-620">
              {ownerData.landOwnerId}
            </p>

            <div className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-sm font-medium border border-green-200" data-id="element-621">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-622" />{' '}
              {t('certificate.verified')}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-623">
            <div className="p-4 border-b border-slate-100 bg-slate-50" data-id="element-624">
              <h4 className="font-bold text-slate-800 flex items-center gap-2" data-id="element-625">
                <SettingsIcon className="w-4 h-4 text-slate-500" data-id="element-626" /> Security
                Settings
              </h4>
            </div>
            <div className="p-4 space-y-4" data-id="element-627">
              <div className="flex justify-between items-center" data-id="element-628">
                <div data-id="element-629">
                  <p className="text-sm font-medium text-slate-800" data-id="element-630">
                    Two-Factor Auth
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-631">Enabled via SMS</p>
                </div>
                <div className="w-10 h-6 bg-[#006A4E] rounded-full relative cursor-pointer" data-id="element-632">
                  <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" data-id="element-633"></div>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-100" data-id="element-634">
                <button className="text-sm text-[#006A4E] font-medium hover:underline" data-id="element-635">
                  Change Account PIN
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Details */}
        <div className="md:col-span-2 space-y-6" data-id="element-636">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-637">
            <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center gap-3" data-id="element-638">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0" data-id="element-639">
                <UserIcon className="w-5 h-5 text-blue-600" data-id="element-640" />
              </div>
              <h3 className="text-lg font-bold text-slate-800" data-id="element-641">
                Personal Information
              </h3>
            </div>
            <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-642">
              <div data-id="element-643">
                <p className="text-sm text-slate-500 mb-1" data-id="element-644">Full Name</p>
                <p className="font-medium text-slate-900" data-id="element-645">{ownerData.name}</p>
              </div>
              <div data-id="element-646">
                <p className="text-sm text-slate-500 mb-1" data-id="element-647">National ID (NID)</p>
                <p className="font-medium text-slate-900" data-id="element-648">{ownerData.nid}</p>
              </div>
              <div data-id="element-649">
                <p className="text-sm text-slate-500 mb-1" data-id="element-650">Father's Name</p>
                <p className="font-medium text-slate-900" data-id="element-651">{ownerData.father}</p>
              </div>
              <div data-id="element-652">
                <p className="text-sm text-slate-500 mb-1" data-id="element-653">Mother's Name</p>
                <p className="font-medium text-slate-900" data-id="element-654">{ownerData.mother}</p>
              </div>
              <div data-id="element-655">
                <p className="text-sm text-slate-500 mb-1" data-id="element-656">Spouse Name</p>
                <p className="font-medium text-slate-900" data-id="element-657">{ownerData.spouse}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-658">
            <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center gap-3" data-id="element-659">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center shrink-0" data-id="element-660">
                <MapPinIcon className="w-5 h-5 text-amber-600" data-id="element-661" />
              </div>
              <h3 className="text-lg font-bold text-slate-800" data-id="element-662">
                Contact Details
              </h3>
            </div>
            <div className="p-5 sm:p-6 space-y-6" data-id="element-663">
              <div className="flex items-start gap-4" data-id="element-664">
                <PhoneIcon className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" data-id="element-665" />
                <div data-id="element-666">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-667">
                    Registered Mobile Number
                  </p>
                  <p className="font-medium text-slate-900" data-id="element-668">
                    {ownerData.mobile}
                  </p>
                  <p className="text-xs text-green-600 mt-1" data-id="element-669">
                    Verified for OTP
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 pt-4 border-t border-slate-100" data-id="element-670">
                <MapPinIcon className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" data-id="element-671" />
                <div data-id="element-672">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-673">
                    Permanent Address
                  </p>
                  <p className="font-medium text-slate-900" data-id="element-674">
                    {ownerData.address}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}